export const UNASSIGNED_GROUP_ID = "__unassigned__"
export const UNASSIGNED_GROUP_TITLE = "Unassigned Campaigns"

export interface ICampaignProjectInfo {
  _id: string
  title: string
  imageUrl: string
}

export interface IProjectGroupMetrics {
  spend: number
  sales: number
  acos: number
  roas: number
  totalCampaigns: number
  activeCampaigns: number
}

export interface IProjectGroup<TCampaign> {
  id: string
  title: string
  imageUrl: string
  isUnassigned: boolean
  campaigns: TCampaign[]
  metrics: IProjectGroupMetrics
}

export interface IProjectGroupAdapter<TCampaign> {
  getProject: (campaign: TCampaign) => ICampaignProjectInfo | null | undefined
  getSpend: (campaign: TCampaign) => number
  getSales: (campaign: TCampaign) => number
  isActive: (campaign: TCampaign) => boolean
}

export function buildProjectGroups<TCampaign>(
  campaigns: TCampaign[],
  adapter: IProjectGroupAdapter<TCampaign>
): IProjectGroup<TCampaign>[] {
  const groupByProjectId = new Map<string, IProjectGroup<TCampaign>>()
  const unassignedCampaigns: TCampaign[] = []

  for (const campaign of campaigns) {
    const project = adapter.getProject(campaign)
    if (!project?._id) {
      unassignedCampaigns.push(campaign)
      continue
    }

    const existingGroup = groupByProjectId.get(project._id)
    if (existingGroup) {
      existingGroup.campaigns.push(campaign)
      continue
    }

    groupByProjectId.set(project._id, {
      id: project._id,
      title: project.title || "Untitled Project",
      imageUrl: project.imageUrl || "",
      isUnassigned: false,
      campaigns: [campaign],
      metrics: emptyMetrics(),
    })
  }

  const groups = [...groupByProjectId.values()]
  if (unassignedCampaigns.length > 0) {
    groups.push({
      id: UNASSIGNED_GROUP_ID,
      title: UNASSIGNED_GROUP_TITLE,
      imageUrl: "",
      isUnassigned: true,
      campaigns: unassignedCampaigns,
      metrics: emptyMetrics(),
    })
  }

  return groups.map((group) => ({
    ...group,
    metrics: calcGroupMetrics(group.campaigns, adapter),
  }))
}

function emptyMetrics(): IProjectGroupMetrics {
  return { spend: 0, sales: 0, acos: 0, roas: 0, totalCampaigns: 0, activeCampaigns: 0 }
}

function calcGroupMetrics<TCampaign>(
  campaigns: TCampaign[],
  adapter: IProjectGroupAdapter<TCampaign>
): IProjectGroupMetrics {
  let spend = 0
  let sales = 0
  let activeCampaigns = 0

  for (const campaign of campaigns) {
    spend += Number(adapter.getSpend(campaign)) || 0
    sales += Number(adapter.getSales(campaign)) || 0
    if (adapter.isActive(campaign)) activeCampaigns += 1
  }

  return {
    spend,
    sales,
    acos: sales > 0 ? (spend / sales) * 100 : 0,
    roas: spend > 0 ? sales / spend : 0,
    totalCampaigns: campaigns.length,
    activeCampaigns,
  }
}

function formatLargeNumber(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(2)}M`
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}k`
  return Number(value || 0).toLocaleString()
}

export function formatGroupCurrency(value: number): string {
  return `$${formatLargeNumber(value)}`
}

export function formatGroupPercent(value: number): string {
  if (!isFinite(value)) return "—"
  return `${value.toFixed(1)}%`
}

export function formatGroupRoas(value: number): string {
  if (!isFinite(value)) return "—"
  return value.toFixed(2)
}
